import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Building, User, UserCheck, Stethoscope } from "lucide-react";
import { SPECIALTY_OPTIONS } from "utils/constants";
import { OnboardingInfoSchema } from "utils/validationSchema";
import { TOOLTIPS } from "utils/tooltips";
import { convertToString } from "utils/form-helpers";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";
import Tooltip from "components/ui/tooltip";

interface PracticeProviderStepProps {
  onNext: (data: any) => void;
  initialData?: any;
}

export default function PracticeProviderStep({
  onNext,
  initialData,
}: PracticeProviderStepProps) {
  const [useSameProvider, setUseSameProvider] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const practiceIndex = 0;
  const providerIndex = 0;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    trigger,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(OnboardingInfoSchema),
    mode: "onChange",
    defaultValues: {
      specialty: "",
      practices: [
        {
          name: "",
          address: "",
          city: "",
          state: "",
          zip: "",
          tax_id: "",
          providers: [
            {
              name: "",
              npi: "",
            },
          ],
        },
      ],
    },
  });

  // Effect to handle initial data population
  useEffect(() => {
    if (initialData) {
      // Use reset with the initial data to ensure form inputs show the values
      // reset(initialData);

      // Set useSameProvider based on whether renderingProvider exists and is different
      if (initialData.practices?.[0]?.renderingProvider) {
        const billingProvider = initialData.practices[0].providers[0];
        const renderingProvider = initialData.practices[0].renderingProvider;

        const isSame =
          billingProvider?.name === renderingProvider?.name &&
          billingProvider?.npi === renderingProvider?.npi;
        setUseSameProvider(isSame);
      } else {
        setUseSameProvider(true);
      }
    }
  }, [initialData, reset]);

  const specialty = watch("specialty");
  const billingProviderName = watch("practices.0.providers.0.name");

  // Handle specialty change
  const handleSpecialtyChange = (value: string) => {
    setValue("specialty", value);
    clearErrors("specialty");
    trigger("specialty");
  };

  // Handle input changes with validation clearing
  const handleInputChange = (fieldName: string, value: string) => {
    setValue(fieldName as any, value);
    clearErrors(fieldName as any);
    // Trigger validation for the specific field after a short delay
    setTimeout(() => {
      trigger(fieldName as any);
    }, 100);
  };

  const onSubmit = async (data: any) => {
    // If using same provider, copy billing provider to rendering provider
    if (useSameProvider) {
      data.practices[0].renderingProvider = {
        name: data.practices[0].providers[0].name,
        npi: data.practices[0].providers[0].npi,
      };
    }

    setIsSubmitting(true);

    try {
      // Submit to /onboarding API like the original OnboardingForm.js
      await baseApi.post("/onboarding", data, { withCredentials: true });

      Mixpanel.track("Practice data submitted", {
        specialty: data.specialty,
        practiceName: data.practices?.[0]?.name,
      });

      toast.success("Practice information saved successfully!");

      // Call onNext to proceed to next step
      onNext(data);
    } catch (error: any) {
      console.error("Practice data submission error:", error);
      Mixpanel.track("Error: Practice data submission", {
        error: error.message,
      });

      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Failed to save practice information. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get nested error messages
  const getFieldError = (fieldPath: string): string | null => {
    const pathArray = fieldPath.split(".");
    let error: any = errors;

    for (const key of pathArray) {
      if (error?.[key]) {
        error = error[key];
      } else {
        return null;
      }
    }

    return (error?.message as string) || null;
  };

  return (
    <div className="max-w-4xl mx-auto pb-16">
      <div className="text-center mb-12">
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-green-50 rounded-2xl">
            <Building className="h-10 w-10 text-green-600" />
          </div>
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Practice & Provider Information
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Tell us about your practice and provider details
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
        {/* Specialty Information */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-gray-200">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-3 bg-teal-50 rounded-xl">
              <Stethoscope className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">
              Specialty Information
            </h3>
          </div>

          <div className="max-w-md">
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Specialty *
              </label>
              <Select onValueChange={handleSpecialtyChange} value={specialty}>
                <SelectTrigger
                  className={`h-12 border-2 ${
                    getFieldError("specialty")
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                >
                  <SelectValue placeholder="Select specialty" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALTY_OPTIONS.map((option) => (
                    <SelectItem
                      key={convertToString(option.value)}
                      value={convertToString(option.value)}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {getFieldError("specialty") && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError("specialty")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Practice Information */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-gray-200">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">
              Practice Information
            </h3>
          </div>

          <div className="space-y-8">
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                Practice Name *
                <span className="ml-1">
                  <Tooltip text={TOOLTIPS.PRACTICE_NAME} />
                </span>
              </label>
              <Input
                placeholder="Enter practice name"
                className={`h-12 border-2 ${
                  getFieldError(`practices.${practiceIndex}.name`)
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                {...register(`practices.${practiceIndex}.name` as const)}
                onChange={(e) =>
                  handleInputChange(
                    `practices.${practiceIndex}.name`,
                    e.target.value
                  )
                }
              />
              {getFieldError(`practices.${practiceIndex}.name`) && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError(`practices.${practiceIndex}.name`)}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                Address *
                <span className="ml-1">
                  <Tooltip text={TOOLTIPS.PRACTICE_ADDRESS} />
                </span>
              </label>
              <Input
                placeholder="Enter practice address"
                className={`h-12 border-2 ${
                  getFieldError(`practices.${practiceIndex}.address`)
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                {...register(`practices.${practiceIndex}.address` as const)}
                onChange={(e) =>
                  handleInputChange(
                    `practices.${practiceIndex}.address`,
                    e.target.value
                  )
                }
              />
              {getFieldError(`practices.${practiceIndex}.address`) && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError(`practices.${practiceIndex}.address`)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  City *
                </label>
                <Input
                  placeholder="City"
                  className={`h-12 border-2 ${
                    getFieldError(`practices.${practiceIndex}.city`)
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                  {...register(`practices.${practiceIndex}.city` as const)}
                  onChange={(e) =>
                    handleInputChange(
                      `practices.${practiceIndex}.city`,
                      e.target.value
                    )
                  }
                />
                {getFieldError(`practices.${practiceIndex}.city`) && (
                  <p className="text-sm text-red-600 font-medium">
                    {getFieldError(`practices.${practiceIndex}.city`)}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  State *
                </label>
                <Input
                  placeholder="State"
                  className={`h-12 border-2 ${
                    getFieldError(`practices.${practiceIndex}.state`)
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                  {...register(`practices.${practiceIndex}.state` as const)}
                  onChange={(e) =>
                    handleInputChange(
                      `practices.${practiceIndex}.state`,
                      e.target.value
                    )
                  }
                />
                {getFieldError(`practices.${practiceIndex}.state`) && (
                  <p className="text-sm text-red-600 font-medium">
                    {getFieldError(`practices.${practiceIndex}.state`)}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Zip Code *
                </label>
                <Input
                  placeholder="Zip code"
                  className={`h-12 border-2 ${
                    getFieldError(`practices.${practiceIndex}.zip`)
                      ? "border-red-500"
                      : "border-gray-200"
                  } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                  {...register(`practices.${practiceIndex}.zip` as const)}
                  onChange={(e) =>
                    handleInputChange(
                      `practices.${practiceIndex}.zip`,
                      e.target.value
                    )
                  }
                />
                {getFieldError(`practices.${practiceIndex}.zip`) && (
                  <p className="text-sm text-red-600 font-medium">
                    {getFieldError(`practices.${practiceIndex}.zip`)}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                Tax ID *
                <span className="ml-1">
                  <Tooltip text={TOOLTIPS.PRACTICE_TAX_ID} />
                </span>
              </label>
              <Input
                placeholder="9-digit Tax ID"
                maxLength={9}
                className={`h-12 border-2 ${
                  getFieldError(`practices.${practiceIndex}.tax_id`)
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                {...register(`practices.${practiceIndex}.tax_id` as const)}
                onChange={(e) =>
                  handleInputChange(
                    `practices.${practiceIndex}.tax_id`,
                    e.target.value
                  )
                }
              />
              {getFieldError(`practices.${practiceIndex}.tax_id`) && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError(`practices.${practiceIndex}.tax_id`)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Billing Provider */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-gray-200">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-3 bg-green-50 rounded-xl">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">
              Billing Provider
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                Provider Name *
                <span className="ml-1">
                  <Tooltip text={TOOLTIPS.BILLING_PROVIDER} />
                </span>
              </label>
              <Input
                placeholder="Provider's full name"
                className={`h-12 border-2 ${
                  getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.name`
                  )
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                {...register(
                  `practices.${practiceIndex}.providers.${providerIndex}.name` as const
                )}
                onChange={(e) =>
                  handleInputChange(
                    `practices.${practiceIndex}.providers.${providerIndex}.name`,
                    e.target.value
                  )
                }
              />
              {getFieldError(
                `practices.${practiceIndex}.providers.${providerIndex}.name`
              ) && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.name`
                  )}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                NPI *
                <span className="ml-1">
                  <Tooltip text={TOOLTIPS.NPI} />
                </span>
              </label>
              <Input
                placeholder="10 digits"
                maxLength={10}
                className={`h-12 border-2 ${
                  getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.npi`
                  )
                    ? "border-red-500"
                    : "border-gray-200"
                } rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors`}
                {...register(
                  `practices.${practiceIndex}.providers.${providerIndex}.npi` as const
                )}
                onChange={(e) =>
                  handleInputChange(
                    `practices.${practiceIndex}.providers.${providerIndex}.npi`,
                    e.target.value
                  )
                }
              />
              {getFieldError(
                `practices.${practiceIndex}.providers.${providerIndex}.npi`
              ) && (
                <p className="text-sm text-red-600 font-medium">
                  {getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.npi`
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Rendering Provider */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-8 border border-gray-200">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-3 bg-purple-50 rounded-xl">
              <UserCheck className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">
              Rendering Provider
            </h3>
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-3 p-4 bg-gray-50/80 rounded-xl border border-gray-200">
              <input
                type="checkbox"
                id="useSameProvider"
                checked={useSameProvider}
                onChange={(e) => setUseSameProvider(e.target.checked)}
                className="h-5 w-5 text-[#005151] focus:ring-[#005151] border-gray-300 rounded"
              />
              <label
                htmlFor="useSameProvider"
                className="text-sm font-semibold text-gray-700 cursor-pointer"
              >
                Same as billing provider
              </label>
            </div>

            {!useSameProvider && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                    Provider Name *
                    <span className="ml-1">
                      <Tooltip text={TOOLTIPS.RENDERING_PROVIDER} />
                    </span>
                  </label>
                  <Input
                    placeholder="Enter rendering provider name"
                    className="h-12 border-2 border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors"
                    {...register("practices.0.renderingProvider.name")}
                    onChange={(e) =>
                      handleInputChange(
                        "practices.0.renderingProvider.name",
                        e.target.value
                      )
                    }
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center">
                    NPI *
                    <span className="ml-1">
                      <Tooltip text={TOOLTIPS.NPI} />
                    </span>
                  </label>
                  <Input
                    placeholder="10-digit NPI"
                    maxLength={10}
                    className="h-12 border-2 border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm focus:border-[#005151] transition-colors"
                    {...register("practices.0.renderingProvider.npi")}
                    onChange={(e) =>
                      handleInputChange(
                        "practices.0.renderingProvider.npi",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            )}

            {useSameProvider && billingProviderName && (
              <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800 font-medium">
                  Rendering provider will be set to:{" "}
                  <span className="font-bold">{billingProviderName}</span>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-8">
          <Button
            type="submit"
            className="px-8 py-3 h-12 bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030] text-white rounded-xl font-semibold shadow-lg transition-all transform hover:scale-105"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Continue"}
          </Button>
        </div>
      </form>
    </div>
  );
}
